import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../../shared/api/apiClient";
import styles from "./TelegramConnectionsPage.module.css";

const formatDateTime = (value, language) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(language || "ru", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};

export default function TelegramConnectionsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [latestAt, setLatestAt] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const language = i18n.resolvedLanguage || i18n.language || "ru";

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get("/auth/telegram/connections", {
        params: { search: search.trim(), limit: 100, offset: 0 },
      });
      const payload = response?.data || {};
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(Number(payload.total || 0));
      setLatestAt(payload.latest_connected_at || null);
      setError("");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          t("telegram_connections_load_error")
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => load(), 180);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => load({ silent: true }), 10000);
    return () => window.clearInterval(timer);
  }, [load]);

  const copyConnectionId = async (value) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(text);
      window.setTimeout(() => setCopiedId((current) => (current === text ? "" : current)), 1600);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopiedId(text);
      window.setTimeout(() => setCopiedId((current) => (current === text ? "" : current)), 1600);
    }
  };

  const latestLabel = useMemo(
    () => formatDateTime(latestAt, language),
    [latestAt, language]
  );

  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <div>
          <button type="button" className={styles.back} onClick={() => navigate("/menu/settings")}>
            ← {t("telegram_connections_back")}
          </button>
          <div className={styles.eyebrow}>{t("telegram_connections_eyebrow")}</div>
          <h1>{t("telegram_connections_title")}</h1>
          <p>{t("telegram_connections_subtitle")}</p>
        </div>
        <button type="button" className={styles.refresh} onClick={() => load()} disabled={loading}>
          ↻ {t("telegram_connections_refresh")}
        </button>
      </div>

      <div className={styles.infoStrip}>
        <div className={styles.infoIcon}>i</div>
        <div>
          <strong>{t("telegram_connections_info_title")}</strong>
          <span>{t("telegram_connections_info_text")}</span>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <span>{t("telegram_connections_total")}</span>
          <strong>{total}</strong>
        </div>
        <div className={styles.statCard}>
          <span>{t("telegram_connections_latest")}</span>
          <strong className={styles.statDate}>{latestLabel}</strong>
        </div>
        <div className={styles.liveCard}>
          <span className={styles.liveDot} />
          <div>
            <strong>{t("telegram_connections_live")}</strong>
            <small>{t("telegram_connections_live_hint")}</small>
          </div>
        </div>
      </div>

      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <div>
            <h2>{t("telegram_connections_history")}</h2>
            <p>{t("telegram_connections_history_hint")}</p>
          </div>
          <label className={styles.searchBox}>
            <span aria-hidden="true">⌕</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("telegram_connections_search")}
            />
          </label>
        </div>

        {error && <div className={styles.error}>{String(error)}</div>}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("telegram_connections_time")}</th>
                <th>{t("telegram_connections_login")}</th>
                <th>{t("telegram_connections_name")}</th>
                <th>{t("telegram_connections_copy_id")}</th>
                <th>{t("telegram_connections_user_id")}</th>
                <th>{t("telegram_connections_count")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && !items.length ? (
                <tr><td colSpan="6" className={styles.empty}>{t("telegram_connections_loading")}</td></tr>
              ) : !items.length ? (
                <tr><td colSpan="6" className={styles.empty}>{t("telegram_connections_empty")}</td></tr>
              ) : items.map((item, index) => {
                const copyValue = item.copy_id || item.telegram_chat_id || item.telegram_user_id;
                const copied = copiedId === String(copyValue || "");
                return (
                  <tr key={item.id} className={index === 0 ? styles.latestRow : ""}>
                    <td data-label={t("telegram_connections_time")}>
                      <strong className={styles.time}>{formatDateTime(item.last_connected_at, language)}</strong>
                      <small>{t("telegram_connections_first_seen")}: {formatDateTime(item.first_connected_at, language)}</small>
                    </td>
                    <td data-label={t("telegram_connections_login")}>
                      <span className={styles.username}>{item.telegram_username || t("telegram_connections_no_username")}</span>
                    </td>
                    <td data-label={t("telegram_connections_name")}>
                      {item.display_name || "—"}
                    </td>
                    <td data-label={t("telegram_connections_copy_id")}>
                      <button
                        type="button"
                        className={`${styles.copyButton} ${copied ? styles.copyButtonDone : ""}`}
                        onClick={() => copyConnectionId(copyValue)}
                        title={t("telegram_connections_copy")}
                      >
                        <code>{copyValue || "—"}</code>
                        <span>{copied ? `✓ ${t("telegram_connections_copied")}` : `⧉ ${t("telegram_connections_copy")}`}</span>
                      </button>
                    </td>
                    <td data-label={t("telegram_connections_user_id")}>
                      <code className={styles.secondaryId}>{item.telegram_user_id || "—"}</code>
                    </td>
                    <td data-label={t("telegram_connections_count")}>
                      <span className={styles.countBadge}>{item.connection_count || 1}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
